/* Page Vérification email (/verifier-email) — Factumis app.
   Landing post-inscription : Better Auth a (côté serveur) envoyé un email de
   confirmation, l'utilisateur doit cliquer le lien pour activer son compte
   (requireEmailVerification: true côté backend).

   Page essentiellement informative. Le bouton « Renvoyer l'email » est en
   best-effort : il appelle authClient.sendVerificationEmail({ email,
   callbackURL }), endpoint exposé car emailVerification est configuré côté
   serveur. Il faut un email pour le renvoi : on le lit en priorité dans la
   session (si l'utilisateur est déjà connecté mais non vérifié), sinon dans
   l'état du routeur ou la query (?email=…) transmis par la page d'inscription.
   Si aucun email n'est disponible, on masque simplement le bouton (pas de
   crash). */
import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { authClient, useSession } from '../lib/auth-client';
import { AuthLayout } from './AuthLayout';
import { Button } from '../components/ui';
import { Icon } from '../lib/icons';

/* Mappe les erreurs Better Auth vers un message FR utilisateur. */
function frenchMessage(error: { code?: string; status?: number }): string {
  if (error.status === 429) return 'Trop de tentatives, réessayez dans un instant.';
  return "Une erreur est survenue, réessayez.";
}

/* Webmail grand public → bouton « Ouvrir … » pour consulter sa boîte en un clic.
   On ne propose le bouton que pour les fournisseurs connus (domaines d'entreprise
   custom → pas de webmail générique à ouvrir). */
const WEBMAILS: Record<string, { label: string; url: string }> = {
  'gmail.com': { label: 'Gmail', url: 'https://mail.google.com/mail/u/0/' },
  'googlemail.com': { label: 'Gmail', url: 'https://mail.google.com/mail/u/0/' },
  'outlook.com': { label: 'Outlook', url: 'https://outlook.live.com/mail/' },
  'outlook.fr': { label: 'Outlook', url: 'https://outlook.live.com/mail/' },
  'hotmail.com': { label: 'Outlook', url: 'https://outlook.live.com/mail/' },
  'hotmail.fr': { label: 'Outlook', url: 'https://outlook.live.com/mail/' },
  'live.fr': { label: 'Outlook', url: 'https://outlook.live.com/mail/' },
  'msn.com': { label: 'Outlook', url: 'https://outlook.live.com/mail/' },
  'yahoo.com': { label: 'Yahoo Mail', url: 'https://mail.yahoo.com/' },
  'yahoo.fr': { label: 'Yahoo Mail', url: 'https://mail.yahoo.com/' },
  'icloud.com': { label: 'iCloud Mail', url: 'https://www.icloud.com/mail/' },
  'me.com': { label: 'iCloud Mail', url: 'https://www.icloud.com/mail/' },
  'orange.fr': { label: 'Orange', url: 'https://webmail.orange.fr/' },
  'wanadoo.fr': { label: 'Orange', url: 'https://webmail.orange.fr/' },
  'free.fr': { label: 'Free', url: 'https://webmail.free.fr/' },
  'sfr.fr': { label: 'SFR', url: 'https://webmail.sfr.fr/' },
  'laposte.net': { label: 'La Poste', url: 'https://www.laposte.net/accueil' },
  'proton.me': { label: 'Proton Mail', url: 'https://mail.proton.me/' },
  'protonmail.com': { label: 'Proton Mail', url: 'https://mail.proton.me/' },
};

function webmailFor(email: string | null): { label: string; url: string } | null {
  if (!email) return null;
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? WEBMAILS[domain] ?? null : null;
}

export default function VerifEmail() {
  const { data: session } = useSession();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // L'email pour le renvoi : session connectée > état routeur > query string.
  const stateEmail = (location.state as { email?: string } | null)?.email;
  const email = session?.user?.email ?? stateEmail ?? searchParams.get('email') ?? null;

  const webmail = webmailFor(email);

  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const busy = loading;

  async function onResend() {
    if (!email) return;
    setErr(null);
    setLoading(true);
    const { error } = await authClient.sendVerificationEmail({
      email,
      callbackURL: '/dashboard',
    });
    setLoading(false);
    if (error) {
      setErr(frenchMessage(error));
      return;
    }
    setSent(true);
  }

  return (
    <AuthLayout
      title="Vérifiez votre email"
      subtitle="Un email de confirmation vous a été envoyé. Cliquez sur le lien pour activer votre compte."
    >
      {err ? (
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          {err}
        </div>
      ) : null}

      {email ? (
        <p className="auth-sub" style={{ marginBottom: 14 }}>
          Envoyé à <b>{email}</b>.
        </p>
      ) : null}

      {webmail ? (
        <a
          href={webmail.url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn--primary"
          style={{ width: '100%', justifyContent: 'center', textDecoration: 'none', marginBottom: 10 }}
        >
          <Icon name="send" size={16} />
          Ouvrir {webmail.label}
        </a>
      ) : null}

      {sent ? (
        <p className="auth-sub">Email renvoyé, consultez vos mails.</p>
      ) : email ? (
        <Button
          type="button"
          variant="soft"
          full
          icon="send"
          loading={loading}
          disabled={busy}
          onClick={onResend}
        >
          Renvoyer l'email
        </Button>
      ) : null}

      <div className="auth-foot-row">
        <Link to="/connexion" className="auth-link">
          Retour à la connexion
        </Link>
      </div>
    </AuthLayout>
  );
}
